import { selectAvailableCapabilityEntries } from '../capabilities/availability.js';
import { PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE } from 'flare-chat-core/contracts/timeline/item-types';
import {
  isAuthoritativeProjection,
  resolveComposerUiProjection,
  resolveRecommendationPanelProjection,
} from '../composer/composerUiProjectionSelector.js';

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

/** Backend-selected actions are mapped directly; this renderer does not decide recommendation eligibility. */
function coerceObject(value = null) {
  return (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
}

function normalizeText(value) {
  return String(value || '').trim();
}

function latestPrimaryFlowPayload(timelineItems = []) {
  const items = Array.isArray(timelineItems) ? timelineItems : [];
  const item = [...items]
    .reverse()
    .find((entry) => normalizeText(entry?.type) === PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE);
  return {
    payload: coerceObject(item?.payload),
    roundKey: normalizeText(item?.roundKey || item?.round_key),
  };
}

function rawActions(payload = {}) {
  if (Array.isArray(payload.next_actions)) {
    return payload.next_actions;
  }
  if (Array.isArray(payload.actions)) {
    return payload.actions;
  }
  return [];
}

export function selectLatestModulePromptRegistry({ timelineItems } = {}) {
  const { payload } = latestPrimaryFlowPayload(timelineItems);
  return Array.isArray(payload.module_prompt_registry) ? payload.module_prompt_registry : [];
}

function promptFromAction(action = {}) {
  const payload = coerceObject(action.payload);
  return [payload.prefill_prompt, action.prefill_prompt, payload.prompt, action.prompt]
    .find((value) => typeof value === 'string' && value.trim()) ?? '';
}

function normalizePromptCardMetadata(source = {}) {
  const value = coerceObject(source);
  const promptCard = coerceObject(firstTruthy(() => (value.promptCard), () => (value.prompt_card)));
  const normalized = {
    surface: normalizeText(firstTruthy(() => (promptCard.surface), () => (value.surface))),
    family: normalizeText(firstTruthy(() => (promptCard.family), () => (value.family), () => (value.cardFamily), () => (value.card_family))),
    iconKey: normalizeText(firstTruthy(() => (promptCard.iconKey), () => (promptCard.icon_key), () => (value.iconKey), () => (value.icon_key))),
    label: normalizeText(firstTruthy(() => (promptCard.label), () => (promptCard.title), () => (value.label), () => (value.title))),
    description: normalizeText(firstTruthy(() => (promptCard.description), () => (promptCard.reason), () => (value.description), () => (value.reason))),
    promptPreview: normalizeText(
      firstTruthy(() => (promptCard.promptPreview), () => (promptCard.prompt_preview), () => (value.promptPreview), () => (value.prompt_preview)),
    ),
  };
  return Object.fromEntries(Object.entries(normalized).filter(([, item]) => item));
}

function promptCardMetadataForAction(action = {}, prompt = '') {
  const payload = coerceObject(action.payload);
  const metadata = normalizePromptCardMetadata(payload.prompt_card || payload.promptCard || action.prompt_card || action.promptCard);
  return { surface: normalizeText(action.surface || payload.surface), ...metadata,
    promptPreview: normalizeText(metadata.promptPreview || payload.prompt_preview || action.prompt_preview || prompt) };
}

function normalizePromptTemplateCard(action = {}, index = 0) {
  const payload = coerceObject(action.payload);
  const prompt = promptFromAction(action);
  if (!prompt) {
    return null;
  }
  const key = normalizeText(firstTruthy(() => (action.action_key), () => (action.id), () => (action.command), () => (`prompt-template-${index + 1}`)));
  const promptCardMetadata = promptCardMetadataForAction(action, prompt);
  return {
    key: `prompt-template:${key}`,
    label: normalizeText(promptCardMetadata.label || action.label || action.text),
    description: normalizeText(promptCardMetadata.description || action.reason || action.description),
    prompt,
    surface: promptCardMetadata.surface,
    family: promptCardMetadata.family,
    iconKey: promptCardMetadata.iconKey,
    promptPreview: promptCardMetadata.promptPreview,
    interactionMode: 'composer_prefill',
    source: 'prompt_template_action',
    actionKey: key,
    action: {
      ...action,
      action_key: key,
      command: normalizeText(firstTruthy(() => (payload.command), () => (action.command))),
      target_mode: normalizeText(firstTruthy(() => (payload.target_mode), () => (action.target_mode))),
      payload,
    },
    moduleKey: normalizeText(action.module_key || payload.module_key),
  };
}

function recommendationPanelActionKeys(recommendationPanel = {}) {
  const values = Array.isArray(recommendationPanel.action_keys)
    ? recommendationPanel.action_keys
    : Array.isArray(recommendationPanel.actionKeys)
      ? recommendationPanel.actionKeys
      : [];
  return new Set(values.map(normalizeText).filter(Boolean));
}

export function selectPromptTemplateCards(options) {
  const { timelineItems, currentTurnRunning, availability } = { currentTurnRunning: false, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  if (currentTurnRunning === true) {
    return [];
  }
  // Committed recommendations belong to the primary-flow projection, not trace presentation.
  const { payload } = latestPrimaryFlowPayload(timelineItems);
  const recommendationPanel = resolveRecommendationPanelProjection(resolveComposerUiProjection(payload));
  if (!isAuthoritativeProjection(recommendationPanel) || recommendationPanel.visible !== true) {
    return [];
  }
  const selected = recommendationPanelActionKeys(recommendationPanel);
  return selectAvailableCapabilityEntries(rawActions(payload), availability, payload.module_prompt_registry)
    .filter((action) => action?.status !== 'blocked' && action?.status !== 'hidden')
    .filter((action) => selected.has(normalizeText(action?.action_key || action?.actionKey || action?.key)))
    .map((action, index) => normalizePromptTemplateCard(coerceObject(action), index))
    .filter(Boolean);
}

export default selectPromptTemplateCards;
